<template>
  <div class="tg-page-root tg-viewport-page min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-purple-50 px-4 py-12 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
    <div class="w-full max-w-sm">
      <!-- Logo/Title Section -->
      <div class="text-center mb-10">
        <div class="flex justify-center mb-4">
          <img src="@/assets/img/logo.png" alt="LeSIM" class="h-16 w-auto drop-shadow-sm" />
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-300">{{ $t('ForgotPassword_Welcome') }}</p>
      </div>

      <!-- Forgot Password Form -->
      <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-white/60 dark:border-gray-700/60 shadow-xl shadow-violet-100/50 dark:shadow-none p-6 space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('ForgotPassword_Email') }}
          </label>
          <div class="flex flex-col gap-2">
            <input
              v-model="email"
              type="email"
              :placeholder="$t('ForgotPassword_Email_Placeholder')"
              class="w-full min-w-0 px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': emailError, 'bg-gray-50 dark:bg-gray-900': codeSent }"
              :disabled="codeSent"
            />
            <button
              v-if="!codeSent"
              type="button"
              @click="sendVerificationCode"
              :disabled="codeLoading || !email"
              class="px-4 py-2.5 text-sm bg-brand text-white rounded-lg font-medium hover:bg-brand/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <span v-if="codeLoading">{{ $t('ForgotPassword_Sending_Code') }}</span>
              <span v-else>{{ $t('ForgotPassword_Send_Code') }}</span>
            </button>
          </div>
          <p v-if="emailError" class="text-red-500 text-xs mt-1">{{ emailError }}</p>
        </div>

        <div v-if="codeSent">
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('ForgotPassword_Verification_Code') }}
          </label>
          <input
            v-model="verificationCode"
            type="text"
            :placeholder="$t('ForgotPassword_Verification_Code_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': codeError }"
            maxlength="6"
          />
          <p v-if="codeError" class="text-red-500 text-xs mt-1">{{ codeError }}</p>
          <p class="text-gray-500 dark:text-gray-300 text-xs mt-1">{{ $t('ForgotPassword_Code_Sent_Message') }}</p>
        </div>

        <div v-if="codeSent">
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('ForgotPassword_New_Password') }}
          </label>
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('ForgotPassword_New_Password_Placeholder')"
              class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all pr-10"
              :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': passwordError }"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400 dark:text-gray-300 transition-colors"
            >
              <svg v-if="showPassword" class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
              <svg v-else class="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
          <p v-if="passwordError" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
        </div>

        <div v-if="codeSent">
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {{ $t('ForgotPassword_Confirm_Password') }}
          </label>
          <input
            v-model="confirmPassword"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('ForgotPassword_Confirm_Password_Placeholder')"
            class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand outline-none transition-all"
            :class="{ 'border-red-400 focus:ring-red-400 focus:border-red-400': confirmPasswordError }"
          />
          <p v-if="confirmPasswordError" class="text-red-500 text-xs mt-1">{{ confirmPasswordError }}</p>
        </div>

        <button
          type="button"
          @click="handleResetPassword"
          :disabled="loading || !codeSent"
          class="w-full bg-brand text-white py-2.5 rounded-lg font-medium text-sm hover:bg-brand/90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="loading">{{ $t('ForgotPassword_Loading') }}</span>
          <span v-else>{{ $t('ForgotPassword_Button') }}</span>
        </button>

        <button
          type="button"
          @click="goToLogin"
          class="w-full border border-brand text-brand py-2.5 rounded-lg font-medium text-sm hover:bg-brand/10 active:scale-[0.98] transition-all duration-200 mt-2"
        >
          {{ $t('ForgotPassword_Back_To_Login') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showAppNotification } from '@/utils/appNotification'
import logger from '@/utils/logger'

const router = useRouter()
const { t } = useI18n()
const api = inject('api')

const email = ref('')
const verificationCode = ref('')
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)

const codeLoading = ref(false)
const codeSent = ref(false)
const loading = ref(false)

const emailError = ref('')
const codeError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')

const validateEmail = () => {
  emailError.value = ''
  if (!email.value) {
    emailError.value = t('ForgotPassword_Email_Required')
    return false
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.value)) {
    emailError.value = t('ForgotPassword_Email_Invalid')
    return false
  }
  return true
}

const sendVerificationCode = async () => {
  if (!validateEmail()) return

  codeLoading.value = true
  try {
    const response = await api.auth.sendResetPasswordCode(email.value)
    const responseData = response.data?.content || response.data
    
    if (responseData?.status === '0') {
      codeSent.value = true
      showAppNotification({
        message: t('ForgotPassword_Code_Sent_Success'),
        type: 'success',
        duration: 2000,
        position: 'bottom-right'
      })
    } else {
      showAppNotification({
        message: responseData?.data?.error || t('ForgotPassword_Code_Send_Failed'),
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
    }
  } catch (error) {
    logger.error('Send code error:', error)
    showAppNotification({
      message: t('ForgotPassword_Code_Send_Failed'),
      type: 'error',
      duration: 2500,
      position: 'bottom-right'
    })
  } finally {
    codeLoading.value = false
  }
}

const validateReset = () => {
  let isValid = true
  codeError.value = ''
  passwordError.value = ''
  confirmPasswordError.value = ''

  if (!verificationCode.value) {
    codeError.value = t('ForgotPassword_Code_Required')
    isValid = false
  }

  if (!password.value) {
    passwordError.value = t('ForgotPassword_Password_Required')
    isValid = false
  } else if (password.value.length < 6) {
    passwordError.value = t('ForgotPassword_Password_Min_Length')
    isValid = false
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = t('ForgotPassword_Confirm_Password_Required')
    isValid = false
  } else if (password.value !== confirmPassword.value) {
    confirmPasswordError.value = t('ForgotPassword_Password_Mismatch')
    isValid = false
  }

  return isValid
}

const handleResetPassword = async () => {
  if (!validateReset()) return

  loading.value = true
  try {
    const data = {
      Email: email.value,
      Password: password.value,
      VerificationCode: verificationCode.value
    }
    const response = await api.auth.resetPassword(data)
    const responseData = response.data?.content || response.data
    logger.debug('Reset password response:', responseData)

    if (String(responseData?.status) === '0' || responseData?.status === 0) {
      showAppNotification({
        message: t('ForgotPassword_Success'),
        type: 'success',
        duration: 3000,
        position: 'bottom-right'
      })
      setTimeout(() => {
        router.push('/login')
      }, 1500)
    } else {
      showAppNotification({
        message: responseData?.data?.error || t('ForgotPassword_Failed'),
        type: 'error',
        duration: 2500,
        position: 'bottom-right'
      })
    }
  } catch (error) {
    logger.error('Reset password error:', error)
    showAppNotification({
      message: t('ForgotPassword_Failed'),
      type: 'error',
      duration: 2500,
      position: 'bottom-right'
    })
  } finally {
    loading.value = false
  }
}

const goToLogin = () => {
  router.push('/login')
}
</script>